import React from 'react';
import {
  FolderTree,
  Building2,
  MapPin,
  Warehouse,
  Package,
  Plus,
  Pencil,
  Trash2,
  ChevronDown,
  ChevronRight,
  Settings,
  Globe,
  Clock,
  Calendar,
  Banknote,
  GripVertical,
} from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import { useAuth } from '../../contexts/AuthContext';
import {
  OrgChartNode as OrgChartNodeType,
  NodeType,
  NODE_COLORS,
  NODE_TYPE_LABELS,
  ALLOWED_CHILDREN,
  LAYOUT_CONSTANTS,
} from './types';
import type { OrgChartDnd } from './useOrgChartDnd';

interface OrgChartNodeProps {
  node: OrgChartNodeType;
  isSelected: boolean;
  isExpanded: boolean;
  hasChildren: boolean;
  onSelect: (node: OrgChartNodeType) => void;
  onToggleExpand: (nodeId: string) => void;
  onAddChild: (parentNode: OrgChartNodeType, childType: NodeType) => void;
  onEdit: (node: OrgChartNodeType) => void;
  onDelete: (node: OrgChartNodeType) => void;
  /** CR-6247: open a company's profile page. Only company nodes have one. */
  onOpenProfile: (node: OrgChartNodeType) => void;
  /** CR-6683: drag-and-drop restructuring — the same handlers the list view
   *  uses, so both views share validation and the move-confirm dialog. */
  dnd?: OrgChartDnd;
  /** CR-8700: Helix only — everyone else gets no Add, Edit or Delete. */
  canEdit: boolean;
  style?: React.CSSProperties;
}

const NODE_ICONS: Record<NodeType, React.ElementType> = {
  group: FolderTree,
  division: Building2,
  company: MapPin,
  warehouse: Warehouse,
  location: Package,
};

export function OrgChartNodeComponent({
  node,
  isSelected,
  isExpanded,
  hasChildren,
  onSelect,
  onToggleExpand,
  onAddChild,
  onEdit,
  onDelete,
  onOpenProfile,
  dnd,
  canEdit,
  style,
}: OrgChartNodeProps) {
  // CR-6683: chart cards are draggable exactly like list rows.
  const draggable = !!dnd && dnd.canDrag(node);
  const isDragActive = !!dnd && dnd.activeId !== null;
  const isDragging = !!dnd && dnd.activeId === node.id;
  const isDropTarget = !!dnd && isDragActive && dnd.canDrop(dnd.activeId!, node.id);
  // CR-6650: Portal Mode is a Valuechain contract decision surfaced in
  // Platform Admin, not something the client configures or needs to see on
  // their own Organisation chart.
  const { user } = useAuth();
  const isHelixUser = !!(user?.is_superuser || user?.is_staff || user?.company_type === 'helix');

  const colors = NODE_COLORS[node.type];
  const Icon = NODE_ICONS[node.type];
  const label = NODE_TYPE_LABELS[node.type];
  const allowedChildren = ALLOWED_CHILDREN[node.type];

  // Get location display
  const locationText = node.metadata?.city
    ? `${node.metadata.city}${node.metadata.country ? `, ${node.metadata.country}` : ''}`
    : node.metadata?.headquarters_country || '';

  // Get effective settings display
  const currencyDisplay = node.effective_settings?.currency?.value ||
    node.regional_settings?.currency || '';
  const timezoneDisplay = node.effective_settings?.timezone?.value ||
    node.regional_settings?.timezone || '';
  const dateFormatDisplay = node.effective_settings?.date_format?.value ||
    node.regional_settings?.date_format || '';

  // Check if settings are inherited vs overridden (has local override).
  // CR-6256: a site now reports its resolved values plus an explicit flag, so
  // populated fields alone no longer mean "overridden" — every inheriting site
  // has them filled in. Group nodes keep the original inference, since their
  // regional_settings still carry only values set on that group.
  const hasOverriddenSettings = node.uses_group_regional_defaults !== undefined
    ? !node.uses_group_regional_defaults
    : (node.regional_settings?.currency ||
      node.regional_settings?.timezone ||
      node.regional_settings?.date_format);

  // Format timezone for display (e.g., "Europe/London" -> "London")
  const shortTimezone = timezoneDisplay ? timezoneDisplay.split('/').pop() : '';

  return (
    <div
      draggable={draggable}
      onDragStart={(e) => {
        // A text selection dragged across cards fires dragstart here too; only
        // a card that can move may start a tree drag, or every card fades.
        if (!draggable) return;
        e.stopPropagation();
        e.dataTransfer.effectAllowed = 'move';
        dnd?.handleDragStart(node.id);
      }}
      onDragEnd={() => dnd?.handleDragEnd(dnd.activeId ?? '', null)}
      onDragOver={(e) => {
        if (isDropTarget) { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }
      }}
      onDrop={(e) => {
        if (!isDropTarget || !dnd?.activeId) return;
        e.preventDefault();
        e.stopPropagation();
        dnd.handleDragEnd(dnd.activeId, node.id);
      }}
      className={cn(
        'absolute cursor-pointer transition-all duration-200 group',
        'rounded-lg border-2 shadow-sm',
        !isDragActive && 'hover:shadow-md',
        colors.bg,
        colors.border,
        isSelected && 'ring-2 ring-primary ring-offset-2',
        draggable && 'cursor-grab active:cursor-grabbing',
        isDragging && 'opacity-40',
        isDropTarget && 'bg-primary/10 ring-2 ring-inset ring-primary',
        // Fade cards that cannot take the drop so valid targets stand out —
        // the same affordance the list view gives.
        isDragActive && !isDropTarget && !isDragging && 'opacity-40',
      )}
      style={{
        ...style,
        width: LAYOUT_CONSTANTS.NODE_WIDTH,
        minHeight: LAYOUT_CONSTANTS.NODE_HEIGHT,
      }}
      onClick={() => onSelect(node)}
      // CR-6247: double-clicking the card opens the company's profile, the way
      // a row in a list does. Without this the second click lands on the text
      // and selects the name, which is what a double click looked like it did.
      onDoubleClick={(e) => {
        if (node.type !== 'company' || !node.entity_id) return;
        e.preventDefault();
        window.getSelection()?.removeAllRanges();
        onOpenProfile(node);
      }}
      title={
        node.type === 'company' && node.entity_id
          ? `Double-click to open ${node.name}`
          : undefined
      }
    >
      {/* Header with type badge and expand/collapse */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-inherit">
        <div className="flex items-center gap-1">
          {draggable && (
            <GripVertical
              className={cn(
                'h-3.5 w-3.5 text-muted-foreground transition-opacity',
                isDragActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-100',
              )}
              aria-hidden
            />
          )}
          <Badge className={cn('text-xs text-white', colors.badge)}>
            {label}
          </Badge>
        </div>
        <div className="flex items-center gap-1">
          {hasOverriddenSettings && (
            <span title="Custom settings">
              <Settings className="h-3 w-3 text-amber-500" />
            </span>
          )}
          {hasChildren && (
            <button
              className="p-0.5 hover:bg-black/10 rounded"
              onClick={(e) => {
                e.stopPropagation();
                onToggleExpand(node.id);
              }}
            >
              {isExpanded ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </button>
          )}
        </div>
      </div>

      {/* Node content */}
      <div className="px-3 py-2 space-y-1">
        <div className="flex items-center gap-2">
          <Icon className={cn('h-4 w-4 flex-shrink-0', colors.text)} />
          {/* CR-6247: a company's name opens its Company Profile page. A group
              or division is a CompanyGroup row with no Company behind it, so it
              stays plain text; Helix keeps the edit pop-up as its detail view. */}
          {node.type === 'company' && node.entity_id ? (
            <button
              type="button"
              className="font-medium text-sm truncate text-left hover:text-primary hover:underline"
              title={`Open ${node.name}`}
              onClick={(e) => {
                e.stopPropagation();
                onOpenProfile(node);
              }}
            >
              {node.name}
            </button>
          ) : (
            <span className="font-medium text-sm truncate" title={node.name}>
              {node.name}
            </span>
          )}
        </div>

        {node.code && (
          <div className="text-xs text-muted-foreground">
            Code: {node.code}
          </div>
        )}

        {locationText && (
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Globe className="h-3 w-3" />
            <span className="truncate">{locationText}</span>
          </div>
        )}

        {/* Regional settings row */}
        {(currencyDisplay || shortTimezone || dateFormatDisplay) && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
            {currencyDisplay && (
              <span className="flex items-center gap-0.5" title={`Currency: ${currencyDisplay}`}>
                <Banknote className="h-3 w-3" />
                {currencyDisplay}
              </span>
            )}
            {shortTimezone && (
              <span className="flex items-center gap-0.5" title={`Timezone: ${timezoneDisplay}`}>
                <Clock className="h-3 w-3" />
                {shortTimezone}
              </span>
            )}
            {dateFormatDisplay && (
              <span className="flex items-center gap-0.5" title={`Date Format: ${dateFormatDisplay}`}>
                <Calendar className="h-3 w-3" />
                {dateFormatDisplay}
              </span>
            )}
          </div>
        )}

        {/* Status badge */}
        {!node.is_active && (
          <Badge variant="secondary" className="text-xs">
            Inactive
          </Badge>
        )}

        {/* Portal mode indicator for companies.
            CR-6650: Portal Mode is decided by Valuechain as part of the
            customer's contract and is owned by Platform Admin > Platform
            Config. The editable field was already dropped from this screen;
            this read-only badge is the last place the client-facing
            Organisation view still surfaced it, so it is now Helix-only.
            Our own team keeps the at-a-glance indicator. */}
        {isHelixUser && node.type === 'company' && node.portal_mode && (
          <Badge variant="outline" className="text-xs">
            {node.portal_mode === 'both' ? 'Both Portals' :
             node.portal_mode === 'customer' ? 'Customer' : 'Supplier'}
          </Badge>
        )}
      </div>

      {/* Action buttons (always visible to Helix). CR-8700: a view-only card
          drops the whole strip rather than leaving an empty bar. */}
      {canEdit && (
        <div className="flex items-center justify-center gap-1 border-t border-inherit px-2 py-1.5 bg-background/50">
          {allowedChildren.length > 0 && (
            <Button
              size="icon"
              variant="ghost"
              className="h-6 w-6"
              onClick={(e) => {
                e.stopPropagation();
                onAddChild(node, allowedChildren[0]);
              }}
              title={`Add ${NODE_TYPE_LABELS[allowedChildren[0]]}`}
            >
              <Plus className="h-3 w-3" />
            </Button>
          )}
          <Button
            size="icon"
            variant="ghost"
            className="h-6 w-6"
            onClick={(e) => {
              e.stopPropagation();
              onEdit(node);
            }}
            title="Edit"
          >
            <Pencil className="h-3 w-3" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="h-6 w-6 text-destructive hover:text-destructive"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(node);
            }}
            title="Delete"
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      )}
    </div>
  );
}

export default OrgChartNodeComponent;
